import { Workbook } from "exceljs";
import mammoth from "mammoth";
import * as pdfjsLib from "pdfjs-dist/legacy/build/pdf.js";
import PdfWorker from "worker-loader!pdfjs-dist/legacy/build/pdf.worker.js";

pdfjsLib.GlobalWorkerOptions.workerPort = new PdfWorker();

export const MAX_AI_CHAT_FILE_SIZE_BYTES = 15 * 1024 * 1024;
const MAX_EXTRACTED_CHARACTERS = 300000;
const MAX_PDF_PAGES = 100;
const TEXT_FILE_EXTENSIONS = new Set([
  ".csv",
  ".json",
  ".log",
  ".md",
  ".sql",
  ".tsv",
  ".txt",
  ".xml",
  ".yaml",
  ".yml",
]);

function fileError(code) {
  const error = new Error(code);
  error.code = code;
  return error;
}

function getExtension(fileName) {
  const normalizedName = String(fileName || "").toLowerCase();
  const lastDot = normalizedName.lastIndexOf(".");
  return lastDot >= 0 ? normalizedName.slice(lastDot) : "";
}

function normalizeExtractedText(text) {
  return String(text || "")
    .replace(/\r\n?/g, "\n")
    .replace(/[\t ]+\n/g, "\n")
    .trim();
}

function formatSections(sections) {
  return sections.map((section) => section.text).filter(Boolean).join("\n\n");
}

function limitExtractedSections(sections, initiallyTruncated = false) {
  let remaining = MAX_EXTRACTED_CHARACTERS;
  let truncated = Boolean(initiallyTruncated);
  const limitedSections = [];

  for (const section of sections) {
    if (remaining <= 0) {
      truncated = true;
      break;
    }

    if (Array.isArray(section.rows)) {
      const rows = [];
      let sheetText = `[Sheet: ${section.label}]`;
      for (const row of section.rows) {
        const line = `Row ${row.number}: ${row.text}`;
        if (sheetText.length + line.length + 1 > remaining) {
          const rowBudget = remaining - sheetText.length - 1;
          if (rowBudget > 0) {
            rows.push({
              ...row,
              text: row.text.slice(0, Math.max(0, rowBudget - `Row ${row.number}: `.length)),
            });
          }
          truncated = true;
          break;
        }
        rows.push(row);
        sheetText += `\n${line}`;
      }

      if (rows.length > 0) {
        const text = `[Sheet: ${section.label}]\n${rows.map((row) => `Row ${row.number}: ${row.text}`).join("\n")}`;
        limitedSections.push({ ...section, rows, text });
        remaining -= text.length + 2;
      }
      if (rows.length < section.rows.length) {
        truncated = true;
        break;
      }
      continue;
    }

    const text = normalizeExtractedText(section.text);
    if (!text) {
      continue;
    }
    const limitedText = text.slice(0, remaining);
    limitedSections.push({ ...section, text: limitedText });
    remaining -= limitedText.length + 2;
    if (limitedText.length < text.length) {
      truncated = true;
      break;
    }
  }

  return {
    sections: limitedSections,
    text: formatSections(limitedSections),
    truncated,
  };
}

async function extractPdfText(file) {
  let loadingTask;
  try {
    loadingTask = pdfjsLib.getDocument({
      data: new Uint8Array(await file.arrayBuffer()),
      isEvalSupported: false,
      useSystemFonts: true,
    });
    const pdf = await loadingTask.promise;
    const pageCount = Math.min(pdf.numPages, MAX_PDF_PAGES);
    const pages = [];

    for (let pageNumber = 1; pageNumber <= pageCount; pageNumber++) {
      const page = await pdf.getPage(pageNumber);
      const content = await page.getTextContent();
      const pageText = content.items
        .map((item) => String(item?.str || "").trim())
        .filter(Boolean)
        .join(" ");
      if (pageText) {
        pages.push({
          kind: "page",
          index: pageNumber,
          label: `Page ${pageNumber}`,
          text: `[Page ${pageNumber}]\n${pageText}`,
        });
      }
    }

    if (pages.length === 0) {
      throw fileError("file_no_extractable_text");
    }

    return limitExtractedSections(pages, pdf.numPages > MAX_PDF_PAGES);
  } catch (error) {
    if (error?.code) {
      throw error;
    }
    throw fileError("file_parse_error");
  } finally {
    if (loadingTask) {
      try {
        await loadingTask.destroy();
      } catch (error) {
        // Ignore cleanup errors after extraction has completed.
      }
    }
  }
}

function getCellText(cell) {
  if (cell.text) {
    return String(cell.text).trim();
  }

  const value = cell.value;
  if (value === null || value === undefined) {
    return "";
  }
  if (value instanceof Date) {
    return value.toISOString();
  }
  if (typeof value === "object") {
    if (Array.isArray(value.richText)) {
      return value.richText.map((part) => part.text || "").join("");
    }
    if (value.result !== undefined) {
      return String(value.result);
    }
    if (value.formula) {
      return `=${value.formula}`;
    }
    if (value.text) {
      return String(value.text);
    }
    return JSON.stringify(value);
  }
  return String(value).trim();
}

async function extractWorkbookText(file) {
  try {
    const workbook = new Workbook();
    await workbook.xlsx.load(await file.arrayBuffer());
    const sheets = [];

    workbook.eachSheet((worksheet) => {
      const rows = [];
      worksheet.eachRow({ includeEmpty: false }, (row, rowNumber) => {
        const cells = [];
        row.eachCell({ includeEmpty: false }, (cell) => {
          const value = getCellText(cell);
          if (value) {
            cells.push(`${cell.address}=${value}`);
          }
        });
        if (cells.length > 0) {
          rows.push({ number: rowNumber, text: cells.join(" | ") });
        }
      });
      if (rows.length > 0) {
        sheets.push({
          kind: "sheet",
          label: worksheet.name,
          rows,
          text: `[Sheet: ${worksheet.name}]\n${rows.map((row) => `Row ${row.number}: ${row.text}`).join("\n")}`,
        });
      }
    });

    if (sheets.length === 0) {
      throw fileError("file_no_extractable_text");
    }
    return limitExtractedSections(sheets);
  } catch (error) {
    if (error?.code) {
      throw error;
    }
    throw fileError("file_parse_error");
  }
}

async function extractDocxText(file) {
  try {
    const result = await mammoth.extractRawText({ arrayBuffer: await file.arrayBuffer() });
    if (!normalizeExtractedText(result.value)) {
      throw fileError("file_no_extractable_text");
    }
    return limitExtractedSections([{
      kind: "text",
      label: "Document",
      text: normalizeExtractedText(result.value),
    }]);
  } catch (error) {
    if (error?.code) {
      throw error;
    }
    throw fileError("file_parse_error");
  }
}

export async function extractFileContent(file) {
  if (!file || !file.name) {
    throw fileError("file_read_error");
  }
  if (Number(file.size) > MAX_AI_CHAT_FILE_SIZE_BYTES) {
    throw fileError("file_too_large");
  }

  const extension = getExtension(file.name);
  let result;

  if (extension === ".pdf") {
    result = await extractPdfText(file);
  } else if (extension === ".xlsx" || extension === ".xlsm") {
    result = await extractWorkbookText(file);
  } else if (extension === ".docx") {
    result = await extractDocxText(file);
  } else if (TEXT_FILE_EXTENSIONS.has(extension) || file.type?.startsWith("text/")) {
    try {
      result = limitExtractedSections([{
        kind: "text",
        label: "Document",
        text: await file.text(),
      }]);
    } catch (error) {
      throw fileError("file_read_error");
    }
  } else {
    throw fileError("file_unsupported");
  }

  if (!result.text) {
    throw fileError("file_no_extractable_text");
  }

  return {
    ...result,
  };
}

function invalidSelection() {
  return fileError("file_selection_invalid");
}

export function selectExtractedFileText(extracted, selection = null) {
  const sections = Array.isArray(extracted?.sections) ? extracted.sections : [];
  if (!selection || typeof selection !== "object") {
    return extracted?.text || "";
  }

  const first = Number(selection.from);
  const last = Number(selection.to);
  if (!Number.isInteger(first) || !Number.isInteger(last) || first < 1 || last < first) {
    throw invalidSelection();
  }

  if (selection.kind === "pages") {
    const pages = sections.filter((section) => section.kind === "page");
    const min = pages.length ? Math.min(...pages.map((section) => section.index)) : 0;
    const max = pages.length ? Math.max(...pages.map((section) => section.index)) : 0;
    if (!pages.length || first < min || last > max) {
      throw invalidSelection();
    }
    const selected = pages.filter((section) => section.index >= first && section.index <= last);
    if (!selected.length) {
      throw invalidSelection();
    }
    return formatSections(selected);
  }

  if (selection.kind === "sheet") {
    const selectedSheet = sections.find((section) => section.kind === "sheet" && section.label === selection.sheetName);
    const rows = selectedSheet?.rows || [];
    const min = rows.length ? Math.min(...rows.map((row) => row.number)) : 0;
    const max = rows.length ? Math.max(...rows.map((row) => row.number)) : 0;
    if (!selectedSheet || !rows.length || first < min || last > max) {
      throw invalidSelection();
    }
    const selectedRows = rows.filter((row) => row.number >= first && row.number <= last);
    if (!selectedRows.length) {
      throw invalidSelection();
    }
    return `[Sheet: ${selectedSheet.label}]\n${selectedRows.map((row) => `Row ${row.number}: ${row.text}`).join("\n")}`;
  }

  if (selection.kind === "lines") {
    const textSection = sections.find((section) => section.kind === "text");
    if (!textSection) {
      throw invalidSelection();
    }
    const lines = textSection.text.split("\n");
    if (last > lines.length) {
      throw invalidSelection();
    }
    return `[Lines ${first}-${last}]\n${lines.slice(first - 1, last).join("\n")}`;
  }

  throw invalidSelection();
}

export function getFileSelectionBounds(extracted, kind, sheetName = "") {
  const sections = Array.isArray(extracted?.sections) ? extracted.sections : [];
  if (kind === "pages") {
    const pages = sections.filter((section) => section.kind === "page").map((section) => section.index);
    return pages.length ? { min: Math.min(...pages), max: Math.max(...pages) } : null;
  }
  if (kind === "sheet") {
    const rows = sections.find((section) => section.kind === "sheet" && section.label === sheetName)?.rows || [];
    const numbers = rows.map((row) => row.number);
    return numbers.length ? { min: Math.min(...numbers), max: Math.max(...numbers) } : null;
  }
  if (kind === "lines") {
    const text = sections.find((section) => section.kind === "text")?.text || "";
    const max = text ? text.split("\n").length : 0;
    return max ? { min: 1, max } : null;
  }
  return null;
}

export function fitFileTextToCharacterBudget(text, characterBudget) {
  const normalized = normalizeExtractedText(text);
  const budget = Math.max(0, Math.floor(Number(characterBudget) || 0));
  if (normalized.length <= budget) {
    return { text: normalized, truncated: false };
  }
  if (budget === 0) {
    return { text: "", truncated: true };
  }

  const marker = "[Content shortened to fit the selected model context.]";
  const contentBudget = Math.max(0, budget - marker.length - 1);
  let end = Math.min(contentBudget, normalized.length);
  const lineBreak = normalized.lastIndexOf("\n", end);
  if (lineBreak > end * 0.6) {
    end = lineBreak;
  }
  const fitted = `${normalized.slice(0, end).trimEnd()}\n${marker}`.slice(0, budget);
  return { text: fitted, truncated: true };
}